import {
  CurrencyValue,
  Chart,
  formatNumber,
  ChartSkeleton,
  formatDatetime,
  assetNameToRgba
} from '@blockanalitica/ui';
import { useMemo, useCallback, ReactNode } from 'react';
import {
  ChartConfig,
  ChartDataset
} from '@blockanalitica/ui/package/types/src/components/charts/Chart';
import { useChartPeriod } from '@/hooks';

export interface DatesData {
  [underlying_symbol: string]: {
    period: string;
    collateral_amount_usd: string;
  }[];
}

export default function DatesLiquidationsGraph({
  data: auctionData,
  timeFilter,
  chartDaysAgo
}: {
  data: DatesData;
  timeFilter?: ReactNode;
  chartDaysAgo: string | number;
}) {
  const [periodInfo, updateChartConfigWithPeriod] =
    useChartPeriod(chartDaysAgo);

  const defaultConfig: ChartConfig = useMemo(
    () => ({
      // @ts-ignore
      type: 'bar',
      options: {
        scales: {
          x: {
            type: 'time',
            stacked: true
          },
          y: {
            stacked: true
          }
        },
        elements: {
          bar: {
            hoverBorderWidth: 0
          }
        }
      }
    }),
    []
  );
  const config = updateChartConfigWithPeriod(defaultConfig);

  const chartDatasets: ChartDataset[] = useMemo(() => {
    return (
      auctionData &&
      Object.entries(auctionData).map(([key, rows]) => ({
        label: key,
        data: rows.map((row) => ({
          x: row['period'],
          y: row['collateral_amount_usd']
        })),
        borderColor: assetNameToRgba(key) || '#fff',
        backgroundColor: assetNameToRgba(key) || '#fff'
      }))
    );
  }, [auctionData]);

  const valueFormatter = useCallback(
    (value: string | number) => (
      <CurrencyValue
        value={value}
        variant="dynamic"
        options={{
          currency: 'USD'
        }}
      />
    ),
    []
  );

  const subValueFormatter = useCallback(
    (value: string | number) => (
      <>
        {`${periodInfo.labelPrefix}${formatDatetime(value, { format: periodInfo.dateFormat })}`}
      </>
    ),
    [periodInfo]
  );

  if (auctionData) {
    return (
      <Chart
        chartDatasets={chartDatasets}
        config={config}
        timeFilter={timeFilter}
        valueFormatter={valueFormatter}
        subValueFormatter={subValueFormatter}
        tooltip={{
          valueFormatter: (value) => `$${formatNumber(Number(value))}`,
          titleFormatter: (_value, tootlipItems) =>
            // @ts-ignore
            `${periodInfo.labelPrefix}${formatDatetime(tootlipItems[0].raw.x, { format: periodInfo.dateFormat })}`
        }}
        showKpisAsTooltip
      />
    );
  }
  return <ChartSkeleton />;
}
